/**
 * @fileoverview Add Owner Provided Materials (OPM) Modal Component
 *
 * Modal form for clients to add materials they are providing for their project.
 * These are items the client purchases directly (not through Red Setter Homes).
 *
 * Features:
 * - Form validation
 * - Date picker for delivery date
 * - Delivery method selection
 * - Loading states during submission
 * - Success/error feedback
 * - Mobile responsive design
 *
 * Note: Image upload functionality will be added in Phase 2 (Cloudinary integration)
 *
 * @requires react
 * @requires react-icons/fa
 * @requires ../hooks/useSelections
 * @requires ./FileUpload
 */

import React, { useState, useEffect } from "react";
import {
  FaTimes,
  FaPlus,
  FaCheck,
  FaExternalLinkAlt,
  FaBoxOpen,
} from "react-icons/fa";
import {
  useAddOwnerPurchase,
  useUpdateOwnerPurchase,
} from "../hooks/useSelections";
import FileUpload from "./FileUpload";
import "./SelectionModals.css";

/**
 * Available delivery method options
 * @constant {Array<Object>}
 */
const DELIVERY_METHODS = [
  { value: "Owner-Delivered to Jobsite", label: "Owner-Delivered to Jobsite" },
  {
    value: "Owner-delivered to Project Manager",
    label: "Owner-Delivered to PM",
  },
  { value: "Delivery Driver", label: "Delivery Driver to Jobsite" },
  { value: "Other", label: "Other" },
];

/**
 * AddOPMModal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Callback to close modal
 * @param {string} props.projectId - Airtable project record ID
 * @param {"add"|"edit"} [props.mode="add"] - Whether the modal creates
 *   a new purchase or edits an existing one
 * @param {Object|null} [props.initialData=null] - Existing purchase row
 *   to edit (as mapped by getOwnerPurchases). Required when mode is "edit"
 * @returns {JSX.Element|null} Modal component or null if closed
 */
const AddOPMModal = ({
  isOpen,
  onClose,
  projectId,
  mode = "add",
  initialData = null,
}) => {
  const isEdit = mode === "edit";

  // Form state
  const [formData, setFormData] = useState({
    productName: "",
    productLink: "",
    quantity: 1,
    roomPlacement: "",
    deliveryDate: "",
    deliveryMethod: "",
    notes: "",
  });

  // Uploaded photos state (edit-mode uploads are additive; existing
  // OPM items carry a single photo. `uploadedPhotos` holds a newly
  // uploaded image (add or replace); `existingPhotos` holds the image
  // already on the record in edit mode, until the client removes it.
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);

  // UI state
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  // Mutation hooks
  const addPurchaseMutation = useAddOwnerPurchase();
  const updatePurchaseMutation = useUpdateOwnerPurchase();
  const activeMutation = isEdit ? updatePurchaseMutation : addPurchaseMutation;

  // The single photo currently attached — a newly uploaded one takes
  // precedence over the record's existing image. When set, it replaces
  // the upload section entirely; when null, the uploader is shown.
  const currentPhoto = uploadedPhotos[0] || existingPhotos[0] || null;

  /**
   * Lock body scroll when modal is open
   */
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [isOpen]);

  /**
   * Reset (add mode) or hydrate from the existing record (edit mode)
   * when the modal opens.
   */
  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && initialData) {
      setFormData({
        productName: initialData.productName || "",
        productLink: initialData.productLink || "",
        quantity: initialData.quantity || 1,
        roomPlacement: initialData.roomPlacement || "",
        // Trim any time component so the date input shows YYYY-MM-DD.
        deliveryDate: (initialData.deliveryDate || "").slice(0, 10),
        deliveryMethod: initialData.deliveryMethod || "",
        notes: initialData.notes || "",
      });
      // Seed the existing photo (at most one) into the FileUpload shape.
      // `type: "image"` is required so the preview renders a thumbnail —
      // the stored `type` is the raw MIME (e.g. "image/jpeg").
      const photos = Array.isArray(initialData.jobsitePhoto)
        ? initialData.jobsitePhoto
        : [];
      setExistingPhotos(
        photos
          .filter((p) => p && (p.url || p.thumbnail))
          .map((p) => ({
            id: p.id,
            url: p.url,
            thumbnail: p.thumbnail,
            filename: p.filename || p.name || "photo",
            type: "image",
          })),
      );
    } else {
      setFormData({
        productName: "",
        productLink: "",
        quantity: 1,
        roomPlacement: "",
        deliveryDate: "",
        deliveryMethod: "",
        notes: "",
      });
      setExistingPhotos([]);
    }
    setUploadedPhotos([]);
    setErrors({});
    setShowSuccess(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /**
   * Handle input field changes
   * @param {Event} e - Change event
   */
  const handleChange = (e) => {
    const { name, value, type } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseInt(value, 10) || 0 : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  /**
   * Handle successful file upload
   * @param {Object} file - Uploaded file data from Cloudinary
   */
  const handleUploadComplete = (file) => {
    setUploadedPhotos((prev) => [...prev, file]);
  };

  /**
   * Handle file upload error
   * @param {Object} error - Error object with file and error message
   */
  const handleUploadError = ({ file, error }) => {
    console.error("Upload error:", file?.name, error);
  };

  /**
   * Remove the currently attached photo, whether it came from the
   * record or was just uploaded. Clearing an existing photo means a
   * saved edit persists the removal (submit sends an empty photo set,
   * which the backend writes as an empty attachment field).
   */
  const handleRemovePhoto = () => {
    setUploadedPhotos([]);
    setExistingPhotos([]);
  };

  /**
   * Validate form data
   * @returns {boolean} Whether form is valid
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.productName.trim()) {
      newErrors.productName = "Product name is required";
    }

    if (formData.quantity < 1) {
      newErrors.quantity = "Quantity must be at least 1";
    }

    if (!formData.roomPlacement.trim()) {
      newErrors.roomPlacement =
        "Please specify where this item will be installed";
    }

    // Validate URL format if provided
    if (formData.productLink && !isValidUrl(formData.productLink)) {
      newErrors.productLink = "Please enter a valid URL";
    }

    // Validate delivery date is not in the past. In edit mode, an
    // unchanged (already-past) date is allowed so old records stay
    // saveable — only a newly entered past date is rejected.
    if (formData.deliveryDate) {
      const originalDate = isEdit ?
        (initialData?.deliveryDate || "").slice(0, 10) :
        "";
      const dateChanged = formData.deliveryDate !== originalDate;

      if (dateChanged) {
        const selectedDate = new Date(formData.deliveryDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDate < today) {
          newErrors.deliveryDate = "Delivery date cannot be in the past";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Check if string is valid URL
   * @param {string} string - String to validate
   * @returns {boolean} Whether string is valid URL
   */
  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  /**
   * Handle form submission
   * @param {Event} e - Submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // OPM items carry a single photo. The final set is the newly
    // uploaded image if one was added, otherwise whatever existing
    // photo remains (empty if the client removed it). The backend
    // overwrites the field with this exact set.
    const source =
      uploadedPhotos.length > 0 ? uploadedPhotos : existingPhotos;
    const photos = source.map((photo) => ({
      url: photo.url,
      filename: photo.filename,
    }));

    try {
      if (isEdit) {
        // The update service reads the delivery date under `deliveryDate`
        // (the add service reads `expectedDelivery`).
        const updates = {
          productName: formData.productName.trim(),
          productLink: formData.productLink.trim() || null,
          quantity: formData.quantity,
          roomPlacement: formData.roomPlacement.trim(),
          deliveryDate: formData.deliveryDate || null,
          deliveryMethod: formData.deliveryMethod || null,
          notes: formData.notes.trim() || null,
          // Always send the complete photo set so removals persist
          // (an empty array clears the photo on the record).
          photos,
        };

        await updatePurchaseMutation.mutateAsync({
          purchaseId: initialData.id,
          projectId,
          updates,
        });
      } else {
        // Prepare purchase data with photo URLs for Airtable
        const purchaseData = {
          productName: formData.productName.trim(),
          productLink: formData.productLink.trim() || null,
          quantity: formData.quantity,
          roomPlacement: formData.roomPlacement.trim(),
          expectedDelivery: formData.deliveryDate || null,
          deliveryMethod: formData.deliveryMethod || null,
          notes: formData.notes.trim() || null,
          // Include uploaded photo URLs for Airtable attachment field
          photos,
        };
        await addPurchaseMutation.mutateAsync({
          projectId,
          ...purchaseData,
        });
      }

      // Show success state
      setShowSuccess(true);

      // Close modal after brief delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Error saving owner purchase:", error);
      setErrors({
        submit:
          error.message ||
          "Failed to save owner provided material. Please try again.",
      });
    }
  };

  /**
   * Handle overlay click to close
   * @param {Event} e - Click event
   */
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  /**
   * Get minimum selectable delivery date. Normally today, but in edit
   * mode we allow the record's existing (possibly past) date so it can
   * be kept without forcing a change.
   * @returns {string} ISO date string (YYYY-MM-DD)
   */
  const getMinDate = () => {
    const today = new Date().toISOString().split("T")[0];
    if (isEdit) {
      const original = (initialData?.deliveryDate || "").slice(0, 10);
      if (original && original < today) {
        return original;
      }
    }
    return today;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="selection-modal modal-md">
        {/* Header */}
        <div className="selection-modal-header">
          <div className="selection-modal-header-left">
            <div className="selection-modal-header-icon">
              <FaBoxOpen />
            </div>
            <h2 className="selection-modal-title">
              {isEdit
                ? "Edit Owner Provided Material"
                : "Add Owner Provided Material"}
            </h2>
          </div>
          <button
            className="selection-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {showSuccess ? (
          <div className="selection-modal-body">
            <div className="selection-modal-success">
              <div className="success-icon">
                <FaCheck />
              </div>
              <h3>{isEdit ? "Material Updated!" : "Material Added!"}</h3>
              <p>Your owner provided material has been saved successfully.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="selection-form">
            <div className="selection-modal-body">
              <div className="selection-form-body">
                {/* Info Banner */}
                <div className="form-info-banner">
                  <p>
                    <strong>Owner-Provided Materials</strong> are items you
                    purchase directly and provide to Red Setter Homes for
                    installation. Please coordinate delivery timing with your
                    project manager.
                  </p>
                </div>
                <div class="form-section">
                  <div className="form-section-title">Product Information</div>
                  {/* Product Name */}
                  <div className="form-group">
                    <label htmlFor="productName" className="form-label">
                      Product Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="productName"
                      name="productName"
                      value={formData.productName}
                      onChange={handleChange}
                      placeholder="e.g., Delta Faucet - Kitchen Sink"
                      className={`form-input ${errors.productName ? "error" : ""}`}
                    />
                    {errors.productName && (
                      <span className="form-error">{errors.productName}</span>
                    )}
                  </div>

                  {/* Product Link */}
                  <div className="form-group">
                    <label htmlFor="productLink" className="form-label">
                      Product Link
                    </label>
                    <div className="input-with-icon">
                      <input
                        type="url"
                        id="productLink"
                        name="productLink"
                        value={formData.productLink}
                        onChange={handleChange}
                        placeholder="https://example.com/product"
                        className={`form-input ${
                          errors.productLink ? "error" : ""
                        }`}
                      />
                      {formData.productLink &&
                        isValidUrl(formData.productLink) && (
                          <a
                            href={formData.productLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="input-icon-link"
                            data-tooltip="Open product link"
                          >
                            <FaExternalLinkAlt />
                          </a>
                        )}
                    </div>
                    {errors.productLink && (
                      <span className="form-error">{errors.productLink}</span>
                    )}
                  </div>

                  {/* Quantity & Room Placement Row */}
                  <div className="form-row">
                    <div className="form-group form-group-half">
                      <label htmlFor="quantity" className="form-label">
                        Quantity <span className="required">*</span>
                      </label>
                      <input
                        type="number"
                        id="quantity"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleChange}
                        min="1"
                        className={`form-input ${errors.quantity ? "error" : ""}`}
                      />
                      {errors.quantity && (
                        <span className="form-error">{errors.quantity}</span>
                      )}
                    </div>

                    <div className="form-group form-group-half">
                      <label htmlFor="roomPlacement" className="form-label">
                        Room/Placement <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="roomPlacement"
                        name="roomPlacement"
                        value={formData.roomPlacement}
                        onChange={handleChange}
                        placeholder="e.g., Primary Bathroom"
                        className={`form-input ${
                          errors.roomPlacement ? "error" : ""
                        }`}
                      />
                      {errors.roomPlacement && (
                        <span className="form-error">
                          {errors.roomPlacement}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div class="form-section">
                  <div className="form-section-title">Delivery Information</div>
                  {/* Delivery Date & Method Row */}
                  <div className="form-row">
                    <div className="form-group form-group-half">
                      <label htmlFor="deliveryDate" className="form-label">
                        Expected Delivery Date
                      </label>
                      <input
                        type="date"
                        id="deliveryDate"
                        name="deliveryDate"
                        value={formData.deliveryDate}
                        onChange={handleChange}
                        min={getMinDate()}
                        className={`form-input ${
                          errors.deliveryDate ? "error" : ""
                        }`}
                      />
                      {errors.deliveryDate && (
                        <span className="form-error">
                          {errors.deliveryDate}
                        </span>
                      )}
                    </div>

                    <div className="form-group form-group-half">
                      <label htmlFor="deliveryMethod" className="form-label">
                        Delivery Method
                      </label>
                      <select
                        id="deliveryMethod"
                        name="deliveryMethod"
                        value={formData.deliveryMethod}
                        onChange={handleChange}
                        className="form-select"
                      >
                        <option value="">Select method...</option>
                        {DELIVERY_METHODS.map((method) => (
                          <option key={method.value} value={method.value}>
                            {method.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div class="form-section">
                  <div className="form-section-title">Other Information</div>
                  {/* Notes */}
                  <div className="form-group">
                    <label htmlFor="notes" className="form-label">
                      Additional Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Special handling instructions, vendor contact info, order confirmation number, etc..."
                      className="form-textarea"
                      rows={4}
                    />
                  </div>
                </div>
                <div class="form-section">
                  <div className="form-section-title">Upload Photo</div>
                  <div className="form-group">
                    <label className="form-label">Jobsite Photo</label>
                    {currentPhoto ? (
                      /* A photo is attached: show it alone, in place of
                         the info banner and uploader. Removing it brings
                         the uploader back. */
                      <div className="opm-photo-preview">
                        <div className="file-upload-preview-content">
                          <img
                            src={currentPhoto.thumbnail || currentPhoto.url}
                            alt={currentPhoto.filename || "Jobsite photo"}
                            className="file-upload-preview-image"
                          />
                          <button
                            type="button"
                            className="file-upload-preview-remove"
                            onClick={handleRemovePhoto}
                            aria-label="Remove photo"
                            data-tooltip="Remove photo"
                          >
                            <FaTimes />
                          </button>
                        </div>
                        <div className="file-upload-preview-info">
                          <span
                            className="file-upload-preview-name"
                            title={currentPhoto.filename}
                          >
                            {currentPhoto.filename || "Jobsite photo"}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Info Banner */}
                        <div className="form-info-banner">
                          <p>
                            A delivery photo helps us identify the product and
                            where it was left on site.
                          </p>
                        </div>
                        <FileUpload
                          onUploadComplete={handleUploadComplete}
                          onUploadError={handleUploadError}
                          allowedTypes={["images"]}
                          multiple={false}
                          folder="owner-purchases"
                          tags={["opm", "owner-purchase", projectId]}
                          label="Upload Product Photo"
                          hint="Add one photo of your purchase or delivery"
                          showCamera={true}
                        />
                      </>
                    )}
                  </div>
                </div>

                {/* Submit Error */}
                {errors.submit && (
                  <div className="form-error-banner">{errors.submit}</div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="selection-modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={onClose}
                disabled={activeMutation.isPending}
                aria-label="Cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={activeMutation.isPending}
              >
                {activeMutation.isPending ? (
                  <>
                    <span className="spinner-small"></span>
                    {isEdit ? "Saving..." : "Adding..."}
                  </>
                ) : (
                  <>
                    {isEdit ? (
                      <>
                        <FaCheck /> Save Changes
                      </>
                    ) : (
                      <>
                        <FaPlus /> Submit Item
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddOPMModal;
